/**
 * Typed fetch wrapper — trimmed copy of frontend/web/src/lib/api-client.ts,
 * pointed at the notification platform. Same {data, meta} envelope, same
 * Bearer injection, same silent refresh-on-401 (refresh goes to the product
 * backend, which issued the token).
 */
import { ApiError, type ApiErrorShape } from '@/lib/api-error';
import { tokenStore } from '@/lib/auth';

const BASE_URL = import.meta.env.VITE_NOTIFY_API_URL ?? '/api';
export const API_ROOT = `${BASE_URL.replace(/\/$/, '')}${BASE_URL.endsWith('/v1') ? '' : '/v1'}`;
const REQUEST_TIMEOUT_MS = 30_000;

export interface ApiMeta {
  page?: number;
  pageSize?: number;
  total?: number;
  limit?: number;
  offset?: number;
  nextCursor?: string | null;
  [key: string]: unknown;
}

export interface Paginated<T> {
  data: T[];
  meta: ApiMeta;
}

export interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
  rawEnvelope?: boolean;
}

let authExpiredHandler: (() => void) | null = null;
function onAuthExpired(fn: () => void) {
  authExpiredHandler = fn;
  return () => {
    authExpiredHandler = null;
  };
}

function withTimeout(signal?: AbortSignal): { signal: AbortSignal; cancel: () => void } {
  const ctrl = new AbortController();
  const timer = setTimeout(
    () => ctrl.abort(new DOMException('Timeout', 'TimeoutError')),
    REQUEST_TIMEOUT_MS,
  );
  if (signal) {
    if (signal.aborted) ctrl.abort(signal.reason);
    else signal.addEventListener('abort', () => ctrl.abort(signal.reason), { once: true });
  }
  return { signal: ctrl.signal, cancel: () => clearTimeout(timer) };
}

function httpFallbackCode(status: number): string {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  return 'internal_error';
}

async function toApiError(res: Response): Promise<ApiError> {
  let body: { error?: ApiErrorShape } | undefined;
  try {
    body = await res.json();
  } catch {
    /* non-JSON error body */
  }
  const e = body?.error;
  return new ApiError(
    e?.code ?? httpFallbackCode(res.status),
    e?.message ?? res.statusText ?? 'Request failed',
    res.status,
    e?.details,
    e?.requestId,
  );
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
  retried = false,
): Promise<T> {
  const token = tokenStore.getAccess();
  const { signal, cancel } = withTimeout(opts.signal);

  let res: Response;
  try {
    res = await fetch(`${API_ROOT}${path}`, {
      method,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...opts.headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } finally {
    cancel();
  }

  if (res.status === 401 && !retried) {
    try {
      await tokenStore.refresh();
      return request<T>(method, path, body, opts, true);
    } catch {
      authExpiredHandler?.();
      throw await toApiError(res);
    }
  }

  if (res.status === 204) return undefined as T;
  if (!res.ok) throw await toApiError(res);

  const json = await res.json();
  return (opts.rawEnvelope ? json : (json.data ?? json)) as T;
}

export const apiClient = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('POST', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PUT', path, body, opts),
  del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, undefined, opts),
  getPage: <T>(path: string, opts?: RequestOptions) =>
    request<Paginated<T>>('GET', path, undefined, { ...opts, rawEnvelope: true }),
  onAuthExpired,
};
